/**
Encode web audio buffer data in a variety of formats. Handles both stereo and
mono.

MP3, WAV

Use web workers where appropriate.

Status: Acceptable
*/

{ type BitDepth } from ./wave-pcm.civet

type MP3EncodeData =
  channelData: Int16Array[]
  sampleRate: number
  kbps?: number

type AudioBufferData = Float32Array[] & {
  sampleRate: number;
}

{ ceil, floor } := Math

// channelData: [Int16Array]
// returns a Promise containing a Blob with type audio/mp3
export mp3Encode := (data: MP3EncodeData) ->
  worker := await remoteWorker("https://danielx.net/composer/lame-worker2.js")

  new Promise<Blob> (resolve, reject) ->
    worker.postMessage data

    worker.onmessage = (e) ->
      resolve e.data

export remoteWorker := (url: string) ->
  fetch(url)
  |> await |> .arrayBuffer()
  // https://github.com/DanielXMoore/Civet/issues/1280
  |> await |> new Blob([.], type: "application/javascript")
  |> URL.createObjectURL
  |> new Worker
  // TODO: revokeObjectURL

export audioChannelDataToInt16 := (buffer: Float32Array) ->
  bufferLength := buffer.length
  data := new Int16Array bufferLength

  // This needs to be inside the function so that it can be used in the worker
  { min, max } := Math

  i .= 0
  while i < bufferLength
    sample .= max -1, min 1, buffer[i]

    if sample < 0
      sample *= 0x8000
    else
      sample *= 0x7FFF

    data[i] = sample
    i++

  return data

export audioBufferToMP3 := (bufferData: AudioBufferData, kbps?: number) ->
  throw new Error "Must pass an AudioBuffer to encode .mp3" unless bufferData

  { sampleRate } := bufferData

  bufferWorker := fnToWorker(audioChannelDataToInt16)

  channelData := await.all bufferData.map (channelBuffer) ->
    bufferWorker(channelBuffer)

  mp3Encode {
    channelData
    sampleRate
    kbps
  }

// 24-bit, not the writer's 16-bit default. A video render attenuates hard before
// this point — the social slots run --gain -24, which puts the mix around
// -28 dBFS — and the delivery pipeline then normalises it back up by about the
// same amount. At 16 bits that leaves only ~11 of the bits carrying signal and
// lifts the quantisation floor to roughly -68 dBFS in the delivered file; at 24
// it lands near -144 dBFS and the gain staging stops mattering. The intermediate
// is discarded after muxing, so the extra bytes cost nothing.
export audioBufferToWav := (channelData: AudioBufferData, bitDepth: BitDepth = 24) ->
  throw new Error "Must pass an AudioBuffer to encode .wav" unless channelData

  new Promise<Blob> (resolve, reject) ->
    worker := new Worker("./lib/wave-worker.js")

    worker.onmessage = (e) ->
      resolve new Blob [e.data.buffer], type: "audio/wav"

    worker.postMessage
      pcmArrays: channelData
      config:
        sampleRate: channelData.sampleRate
        bitDepth: bitDepth

// Convert a function without any closure variables into a worker source
// it receives a single parameter from the onmessage event data, passes it to
// the function, returns the value to the parent, then closes the worker.
export fnToWorker := <D, T>(fn: (data: D) => T) ->
  (data: D) ->
    new Promise<T> (resolve, reject) ->
      workerSource := new Blob [```
        self.onmessage = function (e) {
          self.postMessage((${fn.toString()})(e.data));
          self.close();
        };
      ```], type: "application/javascript"

      url := URL.createObjectURL(workerSource)
      worker := new Worker(url)

      // Resolev and cleanup when we receive the message from the worker.
      worker.onmessage = (e) ->
        resolve(e.data)
        URL.revokeObjectURL(url)

      worker.postMessage data

// Stereo or Mono
export audioBufferToChannelData := (audioBuffer: AudioBuffer, startTime: number, duration: number) ->
  // splice based on start time / end time

  { sampleRate } := audioBuffer

  s := floor startTime * sampleRate
  e := ceil (startTime + duration) * sampleRate

  let r: Float32Array[]
  channels := audioBuffer.numberOfChannels
  if channels is 2
    r = [
      audioBuffer.getChannelData(0).subarray(s, e)
      audioBuffer.getChannelData(1).subarray(s, e)
    ]
  else
    r = [audioBuffer.getChannelData(0).subarray(s, e)]

  return Object.assign r, { sampleRate }
